<template>
  <AuthGuard>
    <v-container>
      <PageHeader title="Reports" :showBack="true" back-to="/customers">
        <v-btn
          class="mb-2"
          color="primary"
          @click="handleClickRefresh"
          :loading="isLoading(reportStore.state.reportsListStatus)"
          :disabled="!hasReportListPermission"
        >
          Refresh
        </v-btn>
      </PageHeader>

      <v-alert v-if="!hasReportListPermission" type="error" variant="tonal" class="mb-6">
        {{ reportListPermissionMessage }}
      </v-alert>

      <v-card class="mb-6 pa-4" elevation="2" variant="text">
        <v-data-table
          :headers="headers"
          :items="reportStore.reports"
          :no-data-text="reportListNoDataText"
          class="bg-transparent"
          :loading="isLoading(reportStore.state.reportsListStatus)"
          :sort-by="[{ key: 'createdAt', order: 'desc' }]"
          :search="state.reportsSearch"
        >
          <template #top>
            <v-text-field
              v-model="state.reportsSearch"
              label="Search Reports"
              prepend-inner-icon="mdi-magnify"
              variant="solo-filled"
              flat
              hide-details
              class="mx-2 my-2"
            />
          </template>

          <template v-slot:item.createdAt="{ item }">
            <span class="text-no-wrap">{{
              moment(item.createdAt).format(Constants.HUMAN_DATE_FORMAT)
            }}</span>
          </template>

          <template v-slot:item.updatedAt="{ item }">
            <span class="text-no-wrap">{{
              moment(item.updatedAt).format(Constants.HUMAN_DATE_FORMAT)
            }}</span>
          </template>

          <template v-slot:item.dateRange="{ item }">
            <div class="tw:lg:flex tw:justify-around!">
              <div style="line-height: 1em">
                <div :style="`color: ${Colors.Green}`" class="text-no-wrap">
                  {{ formatMonthRange(item.dateRange.start, item.dateRange.end) }}
                </div>
                <div>vs</div>
                <div :style="`color: ${Colors.Blue}`" class="text-no-wrap">
                  {{ formatMonthRange(item.comparedRange.start, item.comparedRange.end) }}
                </div>
              </div>
            </div>
          </template>

          <template v-slot:item.showSummary="{ item }">
            <v-icon v-if="item.showSummary !== false">mdi-check</v-icon>
          </template>

          <template v-slot:item.status="{ item }">
            <v-chip :color="getStatusColor(item.status)">
              {{ getStatusLabel(item.status) }}
            </v-chip>
          </template>

          <template v-slot:item.action="{ item }">
            <div class="tw:inline-flex">
              <v-btn
                icon="mdi-content-copy"
                size="40"
                variant="flat"
                @click="handleClickCopyReport(item)"
                :disabled="!hasReportDownloadPermission"
                v-if="item.status === IApp.ReportStatus.Completed"
              ></v-btn>
              <v-btn
                icon="mdi-download"
                size="40"
                variant="flat"
                :href="hasReportDownloadPermission ? getDownloadLink(item) : undefined"
                :target="hasReportDownloadPermission ? '_blank' : undefined"
                :disabled="!hasReportDownloadPermission"
                v-if="item.status === IApp.ReportStatus.Completed"
              ></v-btn>
              <v-btn
                icon="mdi-pencil-outline"
                size="40"
                variant="flat"
                :href="hasReportListPermission ? getDashboardLink(item) : undefined"
                :target="hasReportListPermission ? '_blank' : undefined"
                :disabled="!hasReportListPermission"
                v-if="hasReportConfirmPermission && isReviewableReport(item)"
                @click="handleClickReviewReport"
              ></v-btn>
              <v-btn
                icon="mdi-check"
                color="success"
                size="40"
                variant="plain"
                target="_blank"
                v-if="
                  hasReportConfirmPermission &&
                  _.includes([IApp.ReportStatus.Pending, IApp.ReportStatus.Reviewing], item.status)
                "
                @click="handleClickConfirm(item)"
                :loading="state.isConfirming && state.confirmedReportId === item.id"
                :disabled="state.isConfirming && state.confirmedReportId !== item.id"
                v-tooltip="'Approve'"
              >
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </AuthGuard>
</template>
<script setup lang="ts">
import PageHeader from "@/components/PageHeader.vue";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import { Colors, Constants, URLs } from "@/constants";
import { useAuthStore } from "@/stores/auth";
import { useReportStore } from "@/stores/report";
import Report from "@/actions/Report";
import { IApp } from "@/types";
import {
  apiURL,
  copyTextToClipboard,
  getBearerToken,
  isLoading,
  makeAlert,
  withQueryString
} from "@/utils";
import Auth from "@/utils/auth";
import moment from "moment";
import { computed, onMounted, reactive } from "vue";
import _ from "lodash";

const state = reactive({
  isConfirming: false,
  confirmedReportId: "",
  reportsSearch: ""
});

const reportStore = useReportStore();
const authStore = useAuthStore();

const hasReportConfirmPermission = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_CONFIRM)
);
const hasReportListPermission = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_LIST)
);
const hasReportDownloadPermission = computed(() =>
  authStore.hasPermission(Auth.Permission.REPORT_DOWNLOAD)
);
const reportListPermissionMessage = "You don't have permission to view reports";
const reportDownloadPermissionMessage = "You don't have permission to download reports";
const reportListNoDataText = computed(() =>
  hasReportListPermission.value ? "No reports found" : reportListPermissionMessage
);

const headers = [
  { title: "Title", key: "customerName", sortable: true, align: "center" as const },
  { title: "AI", key: "showSummary", sortable: true, align: "center" as const, width: "50px" },
  { title: "Date Range", key: "dateRange", align: "center" as const },
  { title: "Created At", key: "createdAt", sortable: true, align: "center" as const },
  { title: "Updated At", key: "updatedAt", sortable: true, align: "center" as const },
  { title: "Status", key: "status", sortable: true, align: "center" as const },
  { title: "Actions", key: "action", align: "center" as const, width: "50px" }
];

const getStatusColor = (status: IApp.ReportStatus) => {
  switch (status) {
    case IApp.ReportStatus.Pending:
      return Colors.Blue;
    case IApp.ReportStatus.Reviewing:
      return Colors.Teal;
    case IApp.ReportStatus.InProgress:
      return Colors.Orange;
    case IApp.ReportStatus.Completed:
      return Colors.Green;
    case IApp.ReportStatus.Failed:
      return Colors.Red;
    case IApp.ReportStatus.Confirmed:
      return Colors.Purple;
    case IApp.ReportStatus.GeneratingSummary:
      return Colors.BrightIndigo;
    default:
      return Colors.Grey;
  }
};

const getStatusLabel = (status: IApp.ReportStatus): string => {
  switch (status) {
    case IApp.ReportStatus.Pending:
      return "Waiting For Review";
    case IApp.ReportStatus.Confirmed:
      return "Approved";
    case IApp.ReportStatus.Reviewing:
      return "Waiting For Approval";
    case IApp.ReportStatus.GeneratingSummary:
      return "Generating Summary";
    case IApp.ReportStatus.InProgress:
    case IApp.ReportStatus.Completed:
    case IApp.ReportStatus.Failed:
      return status;
  }
};

const formatMonthRange = (start: string, end: string) => {
  const s = moment(start);
  const e = moment(end);

  if (s.isSame(e, "year")) {
    return `${s.format("MMM")} – ${e.format("MMM")}, ${s.format("YYYY")}`;
  }

  return `${s.format("MMM YYYY")} – ${e.format("MMM YYYY")}`;
};

const handleClickConfirm = async (report: IApp.IReport) => {
  if (state.isConfirming) return;
  state.isConfirming = true;
  state.confirmedReportId = report.id;
  const response = await Report.confirmReport(report.requestId, report.id);
  if (response.status) {
    makeAlert("Report confirmed successfully", "success");
  } else {
    makeAlert(response.message, "error");
  }
  state.isConfirming = false;
};

onMounted(() => {
  if (!hasReportListPermission.value) {
    return;
  }
  reportStore.fetchReports();
});

const isReviewableReport = (item: IApp.IReport) =>
  _.includes(
    [IApp.ReportStatus.Pending, IApp.ReportStatus.Reviewing, IApp.ReportStatus.Completed],
    item.status
  );

const handleClickReviewReport = (event: MouseEvent) => {
  if (!hasReportListPermission.value) {
    event.preventDefault();
    makeAlert(reportListPermissionMessage, "error");
  }
};

const handleClickRefresh = () => {
  if (!hasReportListPermission.value) {
    makeAlert(reportListPermissionMessage, "error");
    return;
  }
  reportStore.fetchReports();
};

const handleClickCopyReport = (item: IApp.IReport) => {
  if (!hasReportDownloadPermission.value) {
    makeAlert(reportDownloadPermissionMessage, "error");
    return;
  }
  copyTextToClipboard(getDownloadLink(item));
};

const getDownloadLink = (item: IApp.IReport) => {
  return withQueryString(apiURL(`reports/${item.downloadId}/download`), {
    token: `Bearer ${getBearerToken()}`
  });
};

const getDashboardLink = (item: IApp.IReport) => {
  const params = {
    "customer-id": [item.customerId],
    preview: true,
    "date-range": item.dateRange,
    "compared-range": item.comparedRange,
    "request-id": item.requestId,
    "report-id": item.id,
    mode: IApp.PrintMode.Edit,
    "show-summary": item.showSummary !== false,
    lazy: true
  };

  return withQueryString(URLs.PAGE_MULTIPLE_CUSTOMER_DASHBOARD, params);
};
</script>

<style scoped>
.v-table :deep(.v-data-table-header__content > span) {
  font-weight: bold;
}
</style>
